import type { ScreenName, Quality } from '../../types'
import type { economy as Economy } from '../../services/economy'
import type { AudioManager } from '../../audio/AudioManager'

/** The surface screens use to navigate and act on the game. Implemented by GameApp. */
export interface AppApi {
  go(screen: ScreenName): void
  /** Begin a fresh story playthrough (plays the intro cutscene on first play, then starts). */
  playStory(): void
  startGame(): void
  resumeGame(): void
  quitToMenu(): void
  restart(): void
  setQuality(q: Quality): void
  /** Toggle browser fullscreen for the whole game surface (no-op where unsupported). */
  toggleFullscreen(): void
  /** Set render resolution: target physical height in px (0 = native panel resolution). */
  setResolution(height: number): void
  /** Commit the finished run's score + jelly to the server (idempotent per run). */
  submitRun(nickname?: string): Promise<void>
  economy: typeof Economy
  audio: AudioManager
  toast(msg: string): void
}

export interface ScreenView {
  el: HTMLElement
  onShow?(): void | Promise<void>
  onHide?(): void
}

type ElOpts = {
  class?: string
  text?: string
  html?: string
  attrs?: Record<string, string>
  style?: Partial<CSSStyleDeclaration>
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (opts.class) node.className = opts.class
  if (opts.text !== undefined) node.textContent = opts.text
  if (opts.html !== undefined) node.innerHTML = opts.html
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v)
  if (opts.style) Object.assign(node.style, opts.style)
  for (const c of children) node.append(c)
  return node
}

export function clear(node: HTMLElement): void {
  node.replaceChildren()
}

export function button(
  label: string,
  opts: { variant?: 'primary' | 'secondary' | 'danger' | 'ghost'; sm?: boolean; onClick?: () => void } = {},
): HTMLButtonElement {
  const cls = ['bubble-btn']
  if (opts.variant && opts.variant !== 'primary') cls.push(`bubble-btn--${opts.variant}`)
  if (opts.sm) cls.push('bubble-btn--sm')
  const b = el('button', { class: cls.join(' '), text: label })
  if (opts.onClick) b.addEventListener('click', opts.onClick)
  return b
}

export function panel(titleText?: string, wide = false): HTMLDivElement {
  const p = el('div', { class: wide ? 'panel panel--wide' : 'panel' })
  if (titleText) p.append(el('h2', { class: 'panel-title', text: titleText }))
  return p
}

export function jellyAmount(n: number): HTMLSpanElement {
  const wrap = el('span', { class: 'jelly-balance' })
  const dot = el('span', {
    style: {
      width: '0.9em',
      height: '0.9em',
      borderRadius: '50% 50% 45% 45%',
      background: 'radial-gradient(circle at 35% 30%, #ffd1ea, #ff69b4)',
      display: 'inline-block',
      boxShadow: '0 0 8px rgba(255,105,180,0.6)',
    },
  })
  wrap.append(dot, el('span', { text: fmt(n) }))
  return wrap
}

export const fmt = (n: number): string => Math.round(n).toLocaleString()
